import { useEffect, useState } from "react";
import "./permission.scss";
import { Button, Form, Modal, Select, Space, Spin, Table } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightArrowLeft, faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import { IUserResult, IUsersResponse } from "../../interfaces/user";
import { UserServices } from "../../services/user";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import { useMessageApi } from "../../context/MessageContext";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permission";
import { useHasPermission } from "../../hooks/useHasPermission";
import { listRoleUser, RoleOption } from "../../utils/helper";
import EditTransferSupplier from "./edit-transfer-supplier.user";

interface ListUserProps {
  onEditUser: (role: IUserResult) => void;
  trigger: boolean;
  onDoneTrigger: () => void;
}

const ROLE_FIELD_MAP: Record<string, keyof IUserResult> = {
  apEscalation: "apEscalation",
  apOwner: "apOwner",
  productHead: "productHead",
  productOwner: "productOwner",
};

const ListUser: React.FC<ListUserProps> = ({ onEditUser, trigger, onDoneTrigger }) => {
  const { user } = useAuth();
  const messageApi = useMessageApi();
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalTransferOpen, setIsModalTransferOpen] = useState(false);
  const [isTransferContentLoading, setIsTransferContentLoading] = useState(false); // ← content loading
const [isSavingTransfer, setIsSavingTransfer] = useState(false);   
  const [users, setUsers] = useState<IUserResult[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [originListUsers, setOriginListUsers] = useState<IUserResult[]>([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<IUserResult>();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0, // update when call api
  });
  const hasPermissionEditor = useHasPermission([PERMISSIONS.PERMISSION_EDITOR]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response: IUsersResponse = await UserServices.getAll();
      if (response.success) {
        setUsers(response.data.results);
        setOriginListUsers(response.data.results);
        setPagination((prev) => ({
          ...prev,
          total: response.data.meta?.totalElements,
        }));

        filterByRole(selectedRoles, response.data.results);
      }
    } catch (error) {
      console.error("Fetch roles error:", error);
    } finally {
      setLoading(false);
      onDoneTrigger();
    }
  };

  useEffect(() => {
    if (trigger) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  const showDeleteModal = (user: IUserResult) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setIsLoadingDelete(true);
    try {
      const res: any = await UserServices.delete(selectedUser.id);
      if (res?.success) {
        messageApi.success("The user has been successfully deleted.");
        setIsModalOpen(false);
        fetchData();
      }
    } catch (error: any) {
      messageApi.error(error?.message);
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const columns = [
    {
      title: "First Name",
      dataIndex: "firstName",
      key: "firstName",
      sorter: (a: IUserResult, b: IUserResult) => a.firstName?.localeCompare(b.firstName),
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
      key: "lastName",
      sorter: (a: IUserResult, b: IUserResult) => a.lastName?.localeCompare(b.lastName),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a: IUserResult, b: IUserResult) => a.email.localeCompare(b.email),
    },
    {
      title: "Role Name",
      dataIndex: "roleName",
      key: "roleName",
      sorter: (a: IUserResult, b: IUserResult) => a.roleName.localeCompare(b.roleName),
    },
    {
      title: "Product Head",
      dataIndex: "productHead",
      key: "productHead",
      render: (_: any, record: IUserResult) =>
        record.productHead ? (
          <>
            <CheckOutlined style={{ color: "green" }} /> ({record.countProductHead})
          </>
        ) : null,
    },
    {
      title: "Product Owner",
      dataIndex: "productOwner",
      key: "productOwner",
      render: (_: any, record: IUserResult) =>
        record.productOwner ? (
          <>
            <CheckOutlined style={{ color: "green" }} /> ({record.countProductOwner})
          </>
        ) : null,
    },
    {
      title: "AP Owner",
      dataIndex: "apOwner",
      key: "apOwner",
      render: (_: any, record: IUserResult) =>
        record.apOwner ? (
          <>
            <CheckOutlined style={{ color: "green" }} /> ({record.countApOwner})
          </>
        ) : null,
    },
    {
      title: "AP Escalation",
      dataIndex: "apEscalation",
      key: "apEscalation",
      render: (_: any, record: IUserResult) =>
        record.apEscalation ? (
          <>
            <CheckOutlined style={{ color: "green" }} /> ({record.countApEscalation})
          </>
        ) : null,
    },
    {
      title: "User Limit",
      dataIndex: "userLimit",
      key: "userLimit",
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      render: (_: any, record: IUserResult) =>
        hasPermissionEditor ? (
          <Space size="small" className="box-action" align="center">
            <Button type="link" onClick={() => showTransferModal(record)}>
              <FontAwesomeIcon icon={faArrowRightArrowLeft} />
            </Button>
            <div>|</div>
            <Button type="link" onClick={() => onEditUser(record)}>
              <FontAwesomeIcon icon={faPen} />
            </Button>
            <div>|</div>
            <Button
              type="link"
              disabled={String(record.id) === user?.id}
              onClick={() => showDeleteModal(record)}
              data-testid={`delete-user-${record.id}`}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Space>
        ) : null,
    },
  ];

  const renderModalDelete = () => (
    <Modal
      title="Confirm delete"
      open={isModalOpen}
      onOk={handleDelete}
      onCancel={handleCancelDelete}
      okText="Delete"
      confirmLoading={isLoadingDelete}
    >
      <p style={{ fontSize: "20px" }}>
        Are you sure you want to delete user <span className="text-primary font-italic">{selectedUser?.email}</span>?{" "}
        <br />
        <br />
        This action cannot be undone.
      </p>
    </Modal>
  );

  const showTransferModal = (user: IUserResult) => {
    setSelectedUser(user);
    setIsModalTransferOpen(true);
    setIsTransferContentLoading(true);
  };

const handleTransferSubmit = async (values: any) => {
  setIsSavingTransfer(true);
  try {
    const res: any = await UserServices.transferSupplier(values);
    if (res?.success) {
      messageApi.success("Supplier transferred to new owner successfully");
      await fetchData();
      setIsModalTransferOpen(false);
      form.resetFields();
    }
  } catch (error: any) {
    messageApi.error(error?.message, 5);
  } finally {
    setIsSavingTransfer(false);
  }
};

const renderModalTransfer = () => (
  <Modal
    title="Transfer Owner"
    open={isModalTransferOpen}
    destroyOnHidden
    style={{ top: 50 }}
    width={650}
    okText="Save"
    okButtonProps={{ loading: isSavingTransfer }}
    confirmLoading={isSavingTransfer}
    onOk={() => form.submit()}
    onCancel={() => {
      form.resetFields(["idNewOwner", "role"]);
      setIsModalTransferOpen(false);
    }}
  >
    <Spin spinning={isTransferContentLoading} >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ role: {}, idNewOwner: null }}
        onFinish={handleTransferSubmit}
      >
        <EditTransferSupplier
          detailUser={selectedUser}
          onLoadingChange={setIsTransferContentLoading}
        />
      </Form>
    </Spin>
  </Modal>
);

  const handleChangeSelectFilter = (value: string[]) => {
    setSelectedRoles(value);
    filterByRole(value);
  };

  const filterByRole = (value: string[], sourceList?: IUserResult[]) => {
    const base = sourceList ?? originListUsers;

    if (value.length > 0) {
      const listNewUser: IUserResult[] = [];

      base.forEach((user) => {
        const hasRole = value.some((role) => {
          const field = ROLE_FIELD_MAP[role];
          return field ? user[field] === true : false;
        });
        if (hasRole) {
          listNewUser.push(user);
        }
      });

      setUsers(listNewUser);
      setPagination((prev) => ({
        ...prev,
        current: 1,
        total: listNewUser.length,
      }));
    } else {
      setUsers([...base]);
      setPagination((prev) => ({
        ...prev,
        current: 1,
        total: base.length,
      }));
    }
  };

  return (
    <>
      <Space
        style={{ width: "100%", marginBottom: "1rem", justifyContent: "end" }}
        direction="horizontal"
        align="center"
      >
        <span>Filter by</span>{" "}
        <Select
          mode="multiple"
          allowClear
          style={{ width: "350px" }}
          placeholder="All Responsibility"
          onChange={handleChangeSelectFilter}
          options={listRoleUser.map((item: RoleOption) => ({
            value: item.value,
            label: item.label,
          }))}
        />
      </Space>

      <Table
        dataSource={users}
        loading={loading}
        rowKey={(record) => record.id}
        columns={columns}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize: pageSize,
            }));
          },
        }}
        size="small"
        className="table-fm"
        rowClassName={(_record, index) => (index % 2 === 0 ? "highlight-row" : "")}
      />
      {renderModalDelete()}
      {renderModalTransfer()}
    </>
  );
};

export default ListUser;
